import type { UrlKeys } from "nuqs";

import { useQuery } from "@tanstack/react-query";
import { Heart } from "lucide-react";
import { parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";

import { FilterBarPlayerList } from "@/components/filter-bar/filter-bar-player-list";
import { PremiumFeatureCard } from "@/components/premium-upsell";
import { FavoritesListSkeleton } from "@/components/skeleton/list-skeleton";
import { buttonVariants } from "@/components/ui/button.styles";
import { useMe } from "@/context/user-context";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { useTRPC } from "@/lib/trpc";
import { cn, getActiveTab } from "@/lib/utils";
import { routes } from "@/routes";

import { PlayerListItem } from "../player-list/player-list-item";
import { PlayerTeaserItem } from "../player-list/player-teaser-item";

type PlayerType = RouterOutputs["player"]["listPlayers"][number];

const searchParams = {
  search: parseAsString.withDefault(""),
  activeTab: parseAsStringLiteral(["matchs", "tournaments"]).withDefault("matchs"),
};
const searchParamsKeys: UrlKeys<typeof searchParams> = {
  search: "s",
  activeTab: "tab",
};

export function ClubFavorites() {
  const trpc = useTRPC();
  const { user } = useMe();
  const isFreemiumClub = user.role === "club" && !user.plan && !user.isAdmin;
  const { data, status } = useQuery(trpc.club.getClubFavoritePlayers.queryOptions(undefined, { enabled: !isFreemiumClub }));
  const { data: teaserFavorites } = useQuery(trpc.club.getClubFavoritePlayersTeaser.queryOptions(undefined, { enabled: isFreemiumClub }));
  const { getCountryName } = useTranslationUtils();
  const [{ search, activeTab }, setSearchFilters] = useQueryStates(searchParams, { urlKeys: searchParamsKeys });
  const { t } = useTranslation(["common", "profile"]);
  const breakpoint = useBreakpoints();

  useEffect(() => {
    setSearchFilters({ activeTab: getActiveTab(user.plan) });
  }, [user, setSearchFilters]);

  // Club freemium ayant déjà repéré des joueurs : on lui rend sa sélection,
  // anonymisée, et l'abonnement devient la suite logique pour lever l'anonymat.
  if (isFreemiumClub && teaserFavorites?.length) {
    const anonymizedFavoritesCount = teaserFavorites.filter(player => !player.isShowcase).length;
    const favoritesCount = teaserFavorites.length;

    return (
      <div className="container flex flex-col gap-4 md:p-4">
        {/* Le décompte porte sur toute la sélection (le club en a favorisé
            autant), mais la bannière disparaît si tout est déjà vitrine : il n'y
            a alors plus rien à déverrouiller. */}
        {anonymizedFavoritesCount > 0 && (
          <div className="flex flex-col items-center gap-3 rounded-[18px] border border-orange-500 bg-orange-100/75 p-6 text-center">
            <div className="flex items-center justify-center rounded-xl bg-orange-500 p-2">
              <Heart className="size-5 text-orange-100" />
            </div>
            <p className="max-w-lg text-balance font-medium">
              {t("common:freemium.favorites-teaser-message", { count: favoritesCount })}
            </p>
            <Link to={routes.plans} className={cn(buttonVariants({ variant: "default" }))}>
              {t("common:freemium.access-all-players")}
            </Link>
          </div>
        )}

        <div className="flex flex-col gap-4 px-2 pb-4 md:gap-0 md:rounded-[18px] md:border md:border-neutral-400 md:p-0">
          {teaserFavorites.map(player => (
            <PlayerTeaserItem key={player.id} player={player} />
          ))}
        </div>
      </div>
    );
  }

  // Club freemium sans favori : on montre à quoi sert la page plutôt qu'un mur.
  if (isFreemiumClub) {
    return (
      <PremiumFeatureCard
        icon={<Heart className="size-6" />}
        title={t("common:freemium.favorites-title")}
        description={t("common:freemium.favorites-description")}
        benefits={[
          t("common:freemium.favorites-benefit-1"),
          t("common:freemium.favorites-benefit-2"),
          t("common:freemium.favorites-benefit-3"),
        ]}
        secondaryLabel={t("common:freemium.browse-players")}
        secondaryTo={routes.players}
      />
    );
  }

  if (status === "pending") {
    return <FavoritesListSkeleton />;
  }
  if (status === "error") {
    return null;
  }

  const filterBySearch = (player: PlayerType, search: string) => {
    const searchTerm = search.toLowerCase();
    return (
      player.fullName?.toLowerCase().includes(searchTerm)
      || getCountryName(player.nationalityCode).toLowerCase().includes(searchTerm)
    );
  };

  const filteredFavoritesPlayers = data.filter(player =>
    filterBySearch(player, search),
  );

  return (
    <div className="container flex flex-col gap-4 md:p-4">
      <FilterBarPlayerList
        listType="favorites"
        search={search}
        activeTab={activeTab}
        setSearch={value => setSearchFilters({ search: value })}
        setActiveTab={value => setSearchFilters({ activeTab: value })}
      />

      <div className="flex flex-col gap-4 px-2 pb-4 md:gap-0 md:rounded-[18px] md:border md:border-neutral-400 md:p-0">
        { (breakpoint === "lg" || breakpoint === "xl") && (
          <div
            className="hidden grid-cols-[294px_50px_220px_242px_116px] items-center gap-3 rounded-t-[18px] bg-neutral-50 px-2.5 py-2 text-xs font-medium text-neutral-500 lg:grid"
          >
            <span className="p-2">
              {`${t("common:mobile-nav.players")} (${filteredFavoritesPlayers.length})`}
            </span>

            <span className="p-2 text-center">
              Nat.
            </span>

            <div className="flex items-center justify-between">
              <span className="w-[72px] px-3 py-2 text-center">
                FFT
              </span>
              <span className="w-[72px] px-3 py-2 text-center">
                UTR
              </span>
              <span className="w-[72px] px-3 py-2 text-center">
                ATP
              </span>
            </div>

            <span className="p-2 text-center">
              {t("profile:availability")}
            </span>

            <span className="p-2 text-center">
              Actions
            </span>
          </div>
        )}

        {
          filteredFavoritesPlayers.length
            ? filteredFavoritesPlayers.map(player => (
                <PlayerListItem key={player.id} player={player} fromPath={routes.favorites} availability={activeTab} />
              ))
            : (
                <span className="rounded-[18px] border border-neutral-400 bg-white p-4 text-center text-neutral-600 md:border-0">
                  {t("common:no-player-found")}
                </span>
              )
        }
      </div>
    </div>
  );
}
